import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Building2, Phone, MapPin, FileText, ArrowRight, CheckCircle, Zap, Crown, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageT } from '../i18n/usePageT';
import { SEO } from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { KycUpload } from '../components/KycUpload';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BecomeSellerPage = () => {
  const { t } = useLanguage();
  const T = usePageT();
  const { isAuthenticated, token, user } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    business_name: '',
    business_description: '',
    phone: '',
    address: '',
    country: 'FR',
    legal_form: '',
    siret: '',
    vat_number: '',
    iban: '',
    bic: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error(T('Connectez-vous d\'abord', 'Please login first'));
      navigate('/login', { state: { from: { pathname: '/become-seller' } } });
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/vendors/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(T('Demande envoyée! Nous allons examiner votre profil.', 'Application submitted! We will review your profile.'));
      
      navigate('/vendor');
    } catch (error) {
      toast.error(error.response?.data?.detail || (T('Erreur', 'Error')));
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: CheckCircle,
      title: T('Paiements Rapides', 'Fast Payments'),
      description: T('Via Stripe Connect sécurisé', 'Via secure Stripe Connect')
    },
    {
      icon: CheckCircle,
      title: T('Tableau de Bord', 'Dashboard'),
      description: T('Gérez vos produits facilement', 'Manage products easily')
    },
    {
      icon: CheckCircle,
      title: T('Support Multilingue', 'Multilingual Support'),
      description: T('Vendez en FR et EN', 'Sell in FR and EN')
    },
    {
      icon: CheckCircle,
      title: T('Vidéo Publicitaire IA', 'AI Ad Videos'),
      description: T('Générez vos pubs en un clic (option)', 'Generate ads in one click (add-on)')
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="become-seller-page">
      {/* Hero */}
      <section className="hero-gradient text-white py-16">
        <div className="container-main">
          <SEO
            title={T('Devenez Vendeur', 'Become a Seller')}
            description={T('Rejoignez Respectful Markets et développez votre activité avec notre plateforme de confiance.', 'Join Respectful Markets and grow your business with our trusted marketplace.')}
            url={`${window.location.origin}/become-seller`}
          />
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {T('Devenez Vendeur', 'Become a Seller')}
            </h1>
            <p className="text-xl text-white/80">
              {T('Rejoignez Respectful Markets et développez votre activité avec notre plateforme de confiance.', 'Join Respectful Markets and grow your business with our trusted marketplace.')}
            </p>
          </div>
        </div>
      </section>

      <div className="container-main py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Benefits */}
          <div>
            <h2 className="text-2xl font-bold mb-6">
              {T('Pourquoi nous rejoindre?', 'Why join us?')}
            </h2>
            <div className="space-y-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex gap-4">
                  <div className="w-10 h-10 bg-[#2A9D8F] rounded-xl flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-white rounded-2xl border border-border/40 p-6">
              <h3 className="font-semibold mb-4">
                {T('Comment ça marche?', 'How it works?')}
              </h3>
              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#0F4C5C] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                  <span>{T('Remplissez le formulaire d\'inscription', 'Fill out the registration form')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#0F4C5C] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                  <span>{T('Notre équipe valide votre profil', 'Our team validates your profile')}</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-6 h-6 bg-[#0F4C5C] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                  <span>{T('Ajoutez vos produits et commencez à vendre!', 'Add your products and start selling!')}</span>
                </li>
              </ol>
            </div>

            {/* Pricing recap */}
            <div className="mt-8" data-testid="become-seller-pricing">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="font-bold text-lg text-[#0F4C5C]">
                  {T('Nos formules', 'Our plans')}
                </h3>
                <a href="/pricing" className="text-sm font-semibold text-[#0F4C5C] hover:text-[#E9C46A] transition-colors flex items-center gap-1">
                  {T('Voir tous les détails →', 'See all details →')}
                </a>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Starter */}
                <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-4 h-4 text-[#2A9D8F]" />
                    <p className="font-bold text-[#0F4C5C]">Starter</p>
                  </div>
                  <p className="text-3xl font-bold text-[#0F4C5C] tracking-tight">
                    {T('Gratuit', 'Free')}
                  </p>
                  <p className="text-xs text-[#0F4C5C]/60 mt-0.5">
                    {T('12% de commission', '12% commission')}
                  </p>
                  <ul className="text-xs text-[#0F4C5C]/80 mt-4 space-y-1.5">
                    <li>• {T('Jusqu\'à 50 produits', 'Up to 50 products')}</li>
                    <li>• {T('KYC simplifié', 'Simplified KYC')}</li>
                    <li>• {T('Support e-mail', 'Email support')}</li>
                  </ul>
                </div>

                {/* Pro */}
                <div className="bg-[#0F4C5C] text-white rounded-2xl p-5 relative overflow-hidden">
                  <span className="absolute top-2 right-2 bg-[#E9C46A] text-[#0F4C5C] text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">
                    {T('Le + populaire', 'Most popular')}
                  </span>
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-4 h-4 text-[#E9C46A]" />
                    <p className="font-bold">Pro</p>
                  </div>
                  <p className="text-3xl font-bold tracking-tight">
                    25€<span className="text-base text-white/70">{T('/mois', '/mo')}</span>
                  </p>
                  <p className="text-xs text-[#E9C46A] mt-0.5">
                    {T('Seulement 3% de commission', 'Just 3% commission')}
                  </p>
                  <ul className="text-xs text-white/85 mt-4 space-y-1.5">
                    <li>• {T('Produits illimités', 'Unlimited products')}</li>
                    <li>• {T('Analytics avancées', 'Advanced analytics')}</li>
                    <li>• {T('Devis B2B', 'B2B quotes')}</li>
                  </ul>
                </div>
              </div>

              {/* AI Video addon teaser */}
              <div className="mt-3 bg-gradient-to-r from-[#E9C46A]/15 to-[#2A9D8F]/15 border border-[#E9C46A]/30 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#E9C46A] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-[#0F4C5C]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#0F4C5C]">
                    {T('Vidéos publicitaires IA — option', 'AI ad videos — add-on')}
                  </p>
                  <p className="text-xs text-[#0F4C5C]/70">
                    {T('19€/mois (10 vidéos) ou 49€/mois illimité · Sora 2', '€19/mo (10 videos) or €49/mo unlimited · Sora 2')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div>
            <div className="bg-white rounded-2xl border border-border/40 p-8">
              <h2 className="text-xl font-bold mb-6">
                {T('Inscription Vendeur', 'Seller Registration')}
              </h2>

              {!isAuthenticated && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                  <p className="text-yellow-800 text-sm">
                    {T('Vous devez être connecté pour vous inscrire comme vendeur.', 'You need to be logged in to register as a seller.')}
                  </p>
                  <Button 
                    className="mt-3 btn-primary" 
                    onClick={() => navigate('/login', { state: { from: { pathname: '/become-seller' } } })}
                  >
                    {t('login')}
                  </Button>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="business_name">
                    {T('Nom de l\'entreprise', 'Business Name')} *
                  </Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      id="business_name"
                      name="business_name"
                      placeholder={T('Ma Boutique', 'My Store')}
                      value={formData.business_name}
                      onChange={handleChange}
                      className="pl-10 h-12"
                      required
                      disabled={!isAuthenticated}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="business_description">
                    {T('Description', 'Description')}
                  </Label>
                  <Textarea
                    id="business_description"
                    name="business_description"
                    placeholder={T('Décrivez votre activité...', 'Describe your business...')}
                    value={formData.business_description}
                    onChange={handleChange}
                    rows={4}
                    disabled={!isAuthenticated}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      {T('Téléphone', 'Phone')}
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="phone"
                        name="phone"
                        placeholder="+33 1 23 45 67 89"
                        value={formData.phone}
                        onChange={handleChange}
                        className="pl-10 h-12"
                        disabled={!isAuthenticated}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="country">
                      {T('Pays', 'Country')} *
                    </Label>
                    <Select
                      value={formData.country}
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                      disabled={!isAuthenticated}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FR">France</SelectItem>
                        <SelectItem value="BE">Belgique</SelectItem>
                        <SelectItem value="CH">Suisse</SelectItem>
                        <SelectItem value="CA">Canada</SelectItem>
                        <SelectItem value="GB">United Kingdom</SelectItem>
                        <SelectItem value="DE">Deutschland</SelectItem>
                        <SelectItem value="ES">España</SelectItem>
                        <SelectItem value="IT">Italia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">
                    {T('Adresse', 'Address')}
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                    <Textarea
                      id="address"
                      name="address"
                      placeholder={T('Adresse complète...', 'Full address...')}
                      value={formData.address}
                      onChange={handleChange}
                      className="pl-10"
                      rows={2}
                      disabled={!isAuthenticated}
                    />
                  </div>
                </div>

                {/* KYC SECTION */}
                <div className="pt-4 mt-2 border-t border-[#0F4C5C]/10">
                  <p className="text-xs uppercase tracking-widest text-[#0F4C5C]/60 font-semibold mb-1">
                    {T('Identité légale (KYC)', 'Legal identity (KYC)')}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {T('Ces informations seront vérifiées avant activation. Elles permettent de respecter nos obligations légales et de vous reverser vos paiements en toute sécurité.', 'These details will be verified before activation. They allow us to comply with legal obligations and pay out your sales securely.')}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="legal_form">{t('legalForm')}</Label>
                    <Select
                      value={formData.legal_form}
                      onValueChange={(value) => setFormData({ ...formData, legal_form: value })}
                      disabled={!isAuthenticated}
                    >
                      <SelectTrigger className="h-12" data-testid="kyc-legal-form">
                        <SelectValue placeholder={T('Choisir', 'Choose')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto-entrepreneur">Auto-entrepreneur</SelectItem>
                        <SelectItem value="ei">EI</SelectItem>
                        <SelectItem value="eurl">EURL</SelectItem>
                        <SelectItem value="sarl">SARL</SelectItem>
                        <SelectItem value="sas">SAS / SASU</SelectItem>
                        <SelectItem value="sa">SA</SelectItem>
                        <SelectItem value="particulier">{T('Particulier (C2C)', 'Individual (C2C)')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="siret">{t('siret')}</Label>
                    <Input
                      id="siret"
                      name="siret"
                      placeholder="14 chiffres"
                      value={formData.siret}
                      onChange={handleChange}
                      className="h-12"
                      disabled={!isAuthenticated}
                      data-testid="kyc-siret"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vat_number">{t('vatNumber')} <span className="text-xs text-muted-foreground">({t('optional')})</span></Label>
                  <Input
                    id="vat_number"
                    name="vat_number"
                    placeholder="FR12345678901"
                    value={formData.vat_number}
                    onChange={handleChange}
                    className="h-12"
                    disabled={!isAuthenticated}
                    data-testid="kyc-vat"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2 sm:col-span-1">
                    <Label htmlFor="iban">{t('iban')}</Label>
                    <Input
                      id="iban"
                      name="iban"
                      placeholder="FR76 …"
                      value={formData.iban}
                      onChange={handleChange}
                      className="h-12 font-mono"
                      disabled={!isAuthenticated}
                      data-testid="kyc-iban"
                    />
                  </div>
                  <div className="space-y-2 col-span-2 sm:col-span-1">
                    <Label htmlFor="bic">{t('bic')}</Label>
                    <Input
                      id="bic"
                      name="bic"
                      placeholder="BNPAFRPP"
                      value={formData.bic}
                      onChange={handleChange}
                      className="h-12 font-mono"
                      disabled={!isAuthenticated}
                      data-testid="kyc-bic"
                    />
                  </div>
                </div>

                {/* KYC document upload (only after registration) */}
                {isAuthenticated && (
                  <div className="space-y-4 pt-2">
                    <KycUpload
                      documentType="kbis"
                      label={t('kbisDocument')}
                      hint={T('Pour les sociétés. Document officiel de moins de 3 mois.', 'For companies. Official document less than 3 months old.')}
                    />
                    <KycUpload
                      documentType="id_document"
                      label={t('idDocument')}
                      hint={T('Pièce d\'identité du dirigeant ou de l\'auto-entrepreneur (recto/verso).', 'ID document of the director or sole trader (front and back).')}
                    />
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full btn-primary h-12 text-lg"
                  disabled={loading || !isAuthenticated}
                >
                  {loading 
                    ? (T('Envoi...', 'Submitting...'))
                    : (T('Soumettre ma demande', 'Submit Application'))}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  {T('En soumettant, vous acceptez nos conditions générales de vente.', 'By submitting, you agree to our terms of service.')}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
